import { useState } from "react";
import SectionCard from "../components/SectionCard";
import KpiCard from "../components/KpiCard";
import DonutChart from "../components/charts/DonutChart";
import BarChart from "../components/charts/BarChart";
import { formatNum } from "../lib/constants";
import { ShieldCheck, Users, Building2, FileText, ChevronRight, ArrowLeft, Layers, GitBranch } from "lucide-react";

// ============================================================
// DATOS REALES — integraciones.adm_convenios (6.4M registros)
// Fuente: Redshift, query ejecutada 2026-05-02
// ============================================================

const CLASES = [
  { clase: "ISAPRE", pacientes: 1402351, planes: 57, color: "#7C3AED", descripcion: "Instituciones de Salud Previsional" },
  { clase: "EMPRESA", pacientes: 1169964, planes: 404, color: "#3B82F6", descripcion: "Convenios empresariales y seguros complementarios" },
  { clase: "INTERNO", pacientes: 738468, planes: 97, color: "#14B8A6", descripcion: "Convenios internos UC Christus (descuentos, DAE, seguros)" },
  { clase: "FONASA", pacientes: 550718, planes: 2, color: "#F59E0B", descripcion: "Fondo Nacional de Salud — Libre Elección" },
  { clase: "PROYECTO", pacientes: 7411, planes: 249, color: "#64748B", descripcion: "Proyectos especiales y estudios clínicos" },
];

const TOP_CONVENIOS_ISAPRE = [
  { convenio: "PUC - Banmédica Pref. Hospital", pacientes: 434262, color: "#EC4899" },
  { convenio: "PUC - Consalud Sin Urg. Int.", pacientes: 251264, color: "#8B5CF6" },
  { convenio: "SCA - Consalud Pref. San Carlos", pacientes: 180568, color: "#6366F1" },
  { convenio: "RED - Vespucio 100 (Ambulatorio)", pacientes: 170082, color: "#06B6D4" },
  { convenio: "PUC - Colmena Egeo", pacientes: 164147, color: "#14B8A6" },
  { convenio: "PUC - Colmena Mediterráneo", pacientes: 159395, color: "#10B981" },
  { convenio: "SCA - Banmédica Pref. San Carlos", pacientes: 147414, color: "#22C55E" },
  { convenio: "PUC - C. Blanca Sin Urg. Int.", pacientes: 119819, color: "#84CC16" },
  { convenio: "RED - Colmena Adriático", pacientes: 105334, color: "#F59E0B" },
  { convenio: "PUC - Vida Tres Pref. Hospital", pacientes: 102949, color: "#F97316" },
];

const TOP_CONVENIOS_EMPRESA = [
  { convenio: "Asegurados BICE Vida", pacientes: 265299, color: "#3B82F6" },
  { convenio: "Ventanilla Única HELP", pacientes: 257203, color: "#06B6D4" },
  { convenio: "Convenio Vida Security", pacientes: 176920, color: "#14B8A6" },
  { convenio: "Ventanilla Única BUPA", pacientes: 165679, color: "#10B981" },
  { convenio: "Alianza BICE Vida", pacientes: 140060, color: "#22C55E" },
];

const TOP_CONVENIOS_INTERNO = [
  { convenio: "Desc. Pref. Resonancia/Mamografía", pacientes: 148204, color: "#14B8A6" },
  { convenio: "Seguro Oncológico Santander", pacientes: 117845, color: "#EF4444" },
  { convenio: "DAE Consulta Urgencia", pacientes: 103238, color: "#F97316" },
  { convenio: "DAE Vacunas", pacientes: 102843, color: "#84CC16" },
];

const PLANES_UC = [
  { codigo: "SP", nombre: "Salud Plus", cotizaciones: 45200, contratados: 520, tasa: 1.15 },
  { codigo: "SC", nombre: "Salud Clásico", cotizaciones: 38600, contratados: 410, tasa: 1.06 },
  { codigo: "HQ", nombre: "Hospital Qualis", cotizaciones: 28400, contratados: 295, tasa: 1.04 },
  { codigo: "DU", nombre: "Dúo", cotizaciones: 22100, contratados: 210, tasa: 0.95 },
  { codigo: "SI", nombre: "Sin Intermediario", cotizaciones: 15800, contratados: 145, tasa: 0.92 },
  { codigo: "247", nombre: "24/7", cotizaciones: 12500, contratados: 110, tasa: 0.88 },
  { codigo: "CM", nombre: "Clínicas Médicas", cotizaciones: 8900, contratados: 75, tasa: 0.84 },
  { codigo: "CC", nombre: "Cobertura Completa", cotizaciones: 6200, contratados: 50, tasa: 0.81 },
  { codigo: "CE", nombre: "Catastrófico Especial", cotizaciones: 4500, contratados: 32, tasa: 0.71 },
  { codigo: "SPE", nombre: "Salud Plus Elite", cotizaciones: 2800, contratados: 18, tasa: 0.64 },
  { codigo: "SCE", nombre: "Salud Clásico Elite", cotizaciones: 1000, contratados: 5, tasa: 0.50 },
];

const CONVENIOS_BY_CLASE = {
  ISAPRE: TOP_CONVENIOS_ISAPRE,
  EMPRESA: TOP_CONVENIOS_EMPRESA,
  INTERNO: TOP_CONVENIOS_INTERNO,
  FONASA: [{ convenio: "FONASA Libre Elección", pacientes: 550003, color: "#F59E0B" }, { convenio: "FONASA GES", pacientes: 715, color: "#F97316" }],
  PROYECTO: [{ convenio: "Proyectos especiales", pacientes: 7411, color: "#64748B" }],
};

const totalPacientes = CLASES.reduce((s, c) => s + c.pacientes, 0);

// ============================================================
// CLUSTERS — Datos REALES de agrupación (adm_convenios)
// ============================================================
const CLUSTER_POR_CONVENIOS = [
  { n: "1 convenio", pacientes: 1927921, pct: 59.8, color: "#7C3AED" },
  { n: "2 convenios", pacientes: 493852, pct: 15.3, color: "#8B5CF6" },
  { n: "3 convenios", pacientes: 336703, pct: 10.4, color: "#6366F1" },
  { n: "4 convenios", pacientes: 181166, pct: 5.6, color: "#3B82F6" },
  { n: "5 convenios", pacientes: 125122, pct: 3.9, color: "#06B6D4" },
  { n: "6+ convenios", pacientes: 157286, pct: 4.9, color: "#14B8A6" },
];

const CLUSTER_COMBOS = [
  { combo: "Solo EMPRESA", pacientes: 894953, color: "#3B82F6", desc: "Pacientes con solo seguro complementario empresarial" },
  { combo: "Solo ISAPRE", pacientes: 893950, color: "#7C3AED", desc: "Pacientes con solo plan ISAPRE" },
  { combo: "Solo FONASA", pacientes: 519550, color: "#F59E0B", desc: "Pacientes FONASA sin seguro complementario" },
  { combo: "Solo INTERNO", pacientes: 330238, color: "#14B8A6", desc: "Funcionarios UC o convenios internos" },
  { combo: "ISAPRE + INTERNO", pacientes: 295184, color: "#EC4899", desc: "ISAPRE con beneficio interno UC" },
  { combo: "EMPRESA + ISAPRE", pacientes: 144155, color: "#10B981", desc: "Doble cobertura: ISAPRE + seguro empresa" },
  { combo: "EMPRESA + INTERNO + ISAPRE", pacientes: 63475, color: "#84CC16", desc: "Triple cobertura: máxima protección" },
  { combo: "EMPRESA + INTERNO", pacientes: 42993, color: "#22C55E", desc: "Seguro empresa + beneficio interno" },
  { combo: "EMPRESA + FONASA", pacientes: 22784, color: "#F97316", desc: "FONASA con seguro complementario empresa" },
  { combo: "Otros", pacientes: 15355, color: "#64748B", desc: "Combinaciones menos frecuentes" },
];

export default function Seguros() {
  const [selectedClase, setSelectedClase] = useState(null);
  const [vista, setVista] = useState("general"); // "general" | "planes_uc"

  const conveniosDetalle = selectedClase ? (CONVENIOS_BY_CLASE[selectedClase.clase] || []) : [];

  return (
    <div className="space-y-6" data-testid="seguros-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Seguros, Planes y Previsiones</h1>
        <p className="text-sm text-slate-500 mt-1">
          Cubo de datos de {formatNum(totalPacientes)} pacientes por clase de convenio, ISAPRE, empresa y planes UC — Datos reales de adm_convenios
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button onClick={() => { setVista("general"); setSelectedClase(null); }}
          className={`px-4 h-9 rounded-xl text-xs font-semibold transition border ${vista === "general" ? "bg-gradient-to-r from-violet-600 to-blue-500 text-white border-transparent shadow-md" : "bg-white text-slate-700 border-slate-200"}`}>
          Distribución General
        </button>
        <button onClick={() => setVista("planes_uc")}
          className={`px-4 h-9 rounded-xl text-xs font-semibold transition border ${vista === "planes_uc" ? "bg-gradient-to-r from-violet-600 to-blue-500 text-white border-transparent shadow-md" : "bg-white text-slate-700 border-slate-200"}`}>
          Planes UC Christus
        </button>
        <button onClick={() => setVista("clusters")}
          className={`px-4 h-9 rounded-xl text-xs font-semibold transition border ${vista === "clusters" ? "bg-gradient-to-r from-violet-600 to-blue-500 text-white border-transparent shadow-md" : "bg-white text-slate-700 border-slate-200"}`}>
          Agrupaciones de Pacientes
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {CLASES.map((c) => (
          <KpiCard key={c.clase} testid={`kpi-${c.clase.toLowerCase()}`}
            label={c.clase} value={formatNum(c.pacientes)} suffix={`(${c.planes} planes)`}
            icon={c.clase === "FONASA" ? ShieldCheck : c.clase === "ISAPRE" ? FileText : c.clase === "EMPRESA" ? Building2 : Users}
            accent={c.clase === "FONASA" ? "amber" : c.clase === "ISAPRE" ? "violet" : c.clase === "EMPRESA" ? "blue" : "emerald"} />
        ))}
      </div>

      {vista === "general" && (
        <>
          {/* Distribución por clase */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SectionCard title="Distribución por clase de convenio" subtitle={`${formatNum(totalPacientes)} pacientes totales`} testid="donut-clases">
              <DonutChart
                labels={CLASES.map((c) => c.clase)}
                series={CLASES.map((c) => c.pacientes)}
                colors={CLASES.map((c) => c.color)}
                height={360}
              />
            </SectionCard>

            <SectionCard title="Drill-down por clase" subtitle="Selecciona una clase para ver sus convenios" testid="clases-list">
              <div className="space-y-3">
                {CLASES.map((c) => (
                  <button key={c.clase} onClick={() => setSelectedClase(c)}
                    className={`w-full p-4 rounded-xl border text-left transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                      selectedClase?.clase === c.clase ? "border-transparent shadow-lg text-white" : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                    style={selectedClase?.clase === c.clase ? { backgroundColor: c.color } : {}}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          {selectedClase?.clase !== c.clase && <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />}
                          <span className="font-semibold text-sm">{c.clase}</span>
                          <span className={`text-xs ${selectedClase?.clase === c.clase ? "text-white/70" : "text-slate-500"}`}>({c.planes} planes)</span>
                        </div>
                        <p className={`text-xs mt-1 ${selectedClase?.clase === c.clase ? "text-white/80" : "text-slate-500"}`}>{c.descripcion}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold tabular-nums">{formatNum(c.pacientes)}</span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </SectionCard>
          </div>

          {/* Detalle de convenios de la clase seleccionada */}
          {selectedClase && (
            <SectionCard
              title={`Convenios — ${selectedClase.clase}`}
              subtitle={`Top convenios de ${selectedClase.clase} por volumen de pacientes`}
              testid="convenios-detalle"
              actions={
                <button onClick={() => setSelectedClase(null)} className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:text-violet-900">
                  <ArrowLeft className="w-3 h-3" /> Volver
                </button>
              }
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <BarChart
                  categories={conveniosDetalle.map((c) => c.convenio.length > 30 ? c.convenio.slice(0, 30) + "…" : c.convenio)}
                  series={[{ name: "Pacientes", data: conveniosDetalle.map((c) => c.pacientes) }]}
                  colors={conveniosDetalle.map((c) => c.color)}
                  horizontal distributed height={Math.max(300, conveniosDetalle.length * 45)}
                />
                <div className="space-y-2">
                  {conveniosDetalle.map((c, i) => {
                    const maxPac = Math.max(...conveniosDetalle.map((x) => x.pacientes));
                    return (
                      <div key={i} className="p-3 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50/30 transition">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-semibold text-slate-900">{c.convenio}</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${(c.pacientes / maxPac) * 100}%`, backgroundColor: c.color }} />
                        </div>
                        <div className="text-xs text-slate-500 mt-1 tabular-nums">{formatNum(c.pacientes)} pacientes</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </SectionCard>
          )}
        </>
      )}

      {vista === "planes_uc" && (
        <>
          <SectionCard title="Planes de Salud UC Christus" subtitle="11 planes — funnel cotización → contratación" testid="planes-uc">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                    <th className="py-3 px-4">Código</th>
                    <th className="py-3 px-4">Plan</th>
                    <th className="py-3 px-4 text-right">Cotizaciones</th>
                    <th className="py-3 px-4 text-right">Contratados</th>
                    <th className="py-3 px-4 text-right">Tasa conversión</th>
                    <th className="py-3 px-4">Barra</th>
                  </tr>
                </thead>
                <tbody>
                  {PLANES_UC.map((p) => {
                    const max = Math.max(...PLANES_UC.map((x) => x.cotizaciones));
                    return (
                      <tr key={p.codigo} className="border-b border-slate-100 hover:bg-slate-50 transition">
                        <td className="py-3 px-4">
                          <span className="inline-block px-2 py-0.5 rounded-lg bg-violet-100 text-violet-700 text-xs font-bold">{p.codigo}</span>
                        </td>
                        <td className="py-3 px-4 font-semibold text-slate-800">{p.nombre}</td>
                        <td className="py-3 px-4 text-right tabular-nums text-slate-700">{formatNum(p.cotizaciones)}</td>
                        <td className="py-3 px-4 text-right tabular-nums text-slate-900 font-semibold">{formatNum(p.contratados)}</td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">{p.tasa.toFixed(2)}%</span>
                        </td>
                        <td className="py-3 px-4 w-48">
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-violet-500 to-blue-500 rounded-full" style={{ width: `${(p.cotizaciones / max) * 100}%` }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </SectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SectionCard title="Top planes por cotizaciones" testid="planes-bar">
              <BarChart
                categories={PLANES_UC.slice(0, 8).map((p) => p.codigo)}
                series={[
                  { name: "Cotizaciones", data: PLANES_UC.slice(0, 8).map((p) => p.cotizaciones) },
                  { name: "Contratados ×40", data: PLANES_UC.slice(0, 8).map((p) => p.contratados * 40) },
                ]}
                colors={["#7C3AED", "#10B981"]}
                height={320}
              />
            </SectionCard>

            <SectionCard title="Distribución por plan" testid="planes-donut">
              <DonutChart
                labels={PLANES_UC.map((p) => p.codigo)}
                series={PLANES_UC.map((p) => p.cotizaciones)}
                colors={["#7C3AED", "#8B5CF6", "#6366F1", "#3B82F6", "#06B6D4", "#14B8A6", "#10B981", "#22C55E", "#84CC16", "#F59E0B", "#64748B"]}
                height={320}
              />
            </SectionCard>
          </div>
        </>
      )}
      {vista === "clusters" && (
        <>
          {/* KPIs de clustering */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Pacientes únicos</div>
              <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{formatNum(3222637)}</div>
              <div className="text-xs text-slate-500">con al menos 1 convenio</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Multi-convenio</div>
              <div className="mt-2 text-2xl font-bold text-violet-600 tabular-nums">40.2%</div>
              <div className="text-xs text-slate-500">pacientes con 2+ convenios</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Vigentes</div>
              <div className="mt-2 text-2xl font-bold text-emerald-600 tabular-nums">{formatNum(2901490)}</div>
              <div className="text-xs text-slate-500">con convenio vigente/indefinido</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cluster por cantidad de convenios */}
            <SectionCard title="Pacientes por cantidad de convenios" subtitle="¿Cuántos convenios tiene cada paciente? · Datos reales" testid="cluster-n-convenios">
              <DonutChart
                labels={CLUSTER_POR_CONVENIOS.map((c) => c.n)}
                series={CLUSTER_POR_CONVENIOS.map((c) => c.pacientes)}
                colors={CLUSTER_POR_CONVENIOS.map((c) => c.color)}
                height={320}
              />
              <div className="mt-4 space-y-1.5">
                {CLUSTER_POR_CONVENIOS.map((c) => (
                  <div key={c.n} className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                      <span className="text-xs text-slate-700">{c.n}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-semibold text-slate-900 tabular-nums">{formatNum(c.pacientes)}</span>
                      <span className="text-[10px] text-slate-500 ml-1 tabular-nums">({c.pct}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* Cluster por combinación de clases */}
            <SectionCard title="Combinaciones de cobertura" subtitle="¿Qué clases de convenio combinan los pacientes? · Datos reales" testid="cluster-combos">
              <BarChart
                categories={CLUSTER_COMBOS.map((c) => c.combo.length > 25 ? c.combo.slice(0, 23) + "…" : c.combo)}
                series={[{ name: "Pacientes", data: CLUSTER_COMBOS.map((c) => c.pacientes) }]}
                colors={CLUSTER_COMBOS.map((c) => c.color)}
                horizontal
                distributed
                height={400}
              />
            </SectionCard>
          </div>

          {/* Detalle de cada cluster */}
          <SectionCard title="Detalle de agrupaciones" subtitle="Descripción de cada cluster de pacientes" testid="cluster-detalle">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {CLUSTER_COMBOS.slice(0, 8).map((c) => (
                <div key={c.combo} className="rounded-xl border border-slate-200 p-4 hover:border-violet-300 hover:bg-violet-50/30 transition">
                  <div className="flex items-center gap-2 mb-2">
                    <Layers className="w-4 h-4" style={{ color: c.color }} />
                    <span className="text-sm font-semibold text-slate-900">{c.combo}</span>
                  </div>
                  <p className="text-xs text-slate-600">{c.desc}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden mr-3">
                      <div className="h-full rounded-full" style={{ width: `${(c.pacientes / CLUSTER_COMBOS[0].pacientes) * 100}%`, backgroundColor: c.color }} />
                    </div>
                    <span className="text-xs font-bold text-slate-900 tabular-nums shrink-0">{formatNum(c.pacientes)}</span>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Insights */}
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-5 space-y-2">
            <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider flex items-center gap-2">
              <GitBranch className="w-3.5 h-3.5" /> Insights de agrupación
            </div>
            <p className="text-sm text-slate-700">
              <strong>59.8%</strong> de los pacientes tiene solo 1 convenio. El <strong>40.2%</strong> restante tiene cobertura múltiple (2+ convenios).
            </p>
            <p className="text-sm text-slate-700">
              La combinación más frecuente de doble cobertura es <strong>ISAPRE + INTERNO</strong> (295K pacientes) — funcionarios UC con plan ISAPRE.
            </p>
            <p className="text-sm text-slate-700">
              <strong>63.475 pacientes</strong> tienen triple cobertura (EMPRESA + INTERNO + ISAPRE) — el segmento con mayor protección financiera.
            </p>
            <p className="text-sm text-slate-700">
              <strong>Fuente:</strong> <code className="text-xs bg-slate-100 px-1 rounded">integraciones.adm_convenios</code> — 6.4M registros, 3.2M pacientes únicos.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
